import { useState, useCallback } from 'react';
import { AuthUser, AuthError, AuthState } from '../types';

export interface AuthStateActions {
    setLoading: (loading: boolean) => void;
    /**
     * Set authentication state.
     * @param authenticated - Whether the user is authenticated
     * @param user - The user data. Required when authenticated=true, should be null when authenticated=false
     */
    setAuthenticated: (authenticated: boolean, user: AuthUser | null) => void;
    setError: (error: AuthError | null) => void;
    clearAuth: () => void;
}

export type UseAuthStateReturn = AuthState & AuthStateActions;

/**
 * Hook to manage authentication state.
 * Separates state management from authentication logic.
 */
export function useAuthState(): UseAuthStateReturn {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<AuthUser | null>(null);
    const [error, setErrorState] = useState<AuthError | null>(null);

    const setLoading = useCallback((loading: boolean) => {
        setIsLoading(loading);
    }, []);

    const setAuthenticated = useCallback((authenticated: boolean, newUser: AuthUser | null) => {
        // Invariant check: authenticated=true requires user data
        if (authenticated && !newUser) {
            console.warn('setAuthenticated called with authenticated=true but no user data provided - treating as unauthenticated');
            setIsAuthenticated(false);
            setUser(null);
            return;
        }
        setIsAuthenticated(authenticated);
        setUser(newUser);
        // Clear any previous error on successful authentication
        if (authenticated) {
            setErrorState(null);
        }
    }, []);

    const setError = useCallback((newError: AuthError | null) => {
        setErrorState(newError);
    }, []);

    const clearAuth = useCallback(() => {
        setIsAuthenticated(false);
        setUser(null);
        setErrorState(null);
    }, []);

    return {
        isLoading,
        isAuthenticated,
        user,
        error,
        setLoading,
        setAuthenticated,
        setError,
        clearAuth,
    };
}
